import { get } from "lodash";
import { AxiosError, AxiosResponse } from "axios";

// Custom
import { Group } from "../../utils/logger";

// Get API Configs
import InternalApi from "../../utils/axios.InternalConfig";

// Types
import { Dispatch } from "redux";

import {
  LOAD_CPP,
  SET_CPP,
  CPP_LOADING,
  CPP_ERROR,
  AUTH_ERROR_TITLE,
  AUTH_ERROR_MSG,
  CPP_ERROR_TITLE,
  CPP_ERROR_MSG,
  AUTH_TOKEN,
} from "../constants";
import { CPP } from "../../types/cpp";

import { toastr } from "react-redux-toastr";

const LoggerGroup = new Group("CPP Actions", { disable: false });

// ================================================================================================
// CPP Operations =================================================================================
// ================================================================================================

let cppCallInProgress: any = null;
export const getCPP = () => (dispatch: Dispatch) => {
  if (cppCallInProgress) {
    LoggerGroup.emit("Getting Cached CPP Call");
    return cppCallInProgress;
  }

  dispatch({ type: CPP_LOADING });

  LoggerGroup.emit("Getting CPP");

  cppCallInProgress = InternalApi.get(`/cpp`)
    .then(handleSuccess) // Handle Success Response
    .catch(handleError) // Handle Error Response
    .finally(() => {
      // Clear call to allow for CPP changes (i.e. changing companies)
      cppCallInProgress = null;
    });

  return cppCallInProgress;

  function handleSuccess(res: AxiosResponse) {
    dispatch({ type: LOAD_CPP, payload: res.data });
    LoggerGroup.emit("LOAD CPP", { table: res.data }).end();
  }

  function handleError(err: AxiosError) {
    const error = get(err, "response.data.errors") || err.message;
    dispatch({ type: CPP_ERROR, payload: error });

    LoggerGroup.emit({ warn: "Get CPP Error" }) // Custom error log
      .emit({ error }) // Log the Axios Error
      .end(); // Send to console immediately

    if (error.includes(401)) {
      toastr.error(AUTH_ERROR_TITLE, AUTH_ERROR_MSG);
    } else {
      toastr.error(error, err.message);
    }
  }
};

export const setCPP = (cpp: CPP) => (dispatch: Dispatch) => {
  LoggerGroup.emit("Setting CPP");

  dispatch({ type: SET_CPP, payload: cpp });
};

export const refreshCPP = (cpp: CPP) => (dispatch: Dispatch) => {
  LoggerGroup.emit("Refreshing CPP Token");

  // refresh token here
  return InternalApi.get("/cpp/token", {
    params: {
      domain: cpp.emailDomain,
    },
  })
    .then(handleSuccess) // Handle Success Response
    .catch(handleError); // Handle Error Response

  function handleSuccess(res: AxiosResponse) {
    dispatch({ type: SET_CPP, payload: cpp });
    dispatch({ type: AUTH_TOKEN, payload: res.data.token });
    LoggerGroup.emit("Refreshed CPP Token", { table: res.data }).end();
  }

  function handleError(err: AxiosError) {
    const error = get(err, "response.data.errors") || err.message;
    dispatch({ type: CPP_ERROR, payload: error });

    LoggerGroup.emit({ warn: "Refreshing CPP Token Error" }) // Custom error log
      .emit({ error }) // Log the Axios Error
      .end(); // Send to console immediately

    if (error.includes(401)) {
      toastr.error(AUTH_ERROR_TITLE, AUTH_ERROR_MSG);
    } else {
      toastr.error(error, err.message);
    }
  }
};

export const getCppUri =
  (accountNumber: string, parentAccount?: string) => async (dispatch: any) => {
    LoggerGroup.emit(`[i] Getting iframe uri for cpp`);

    let uri = `/cpp/uri?accountNumber=${accountNumber}`;

    if (parentAccount) {
      uri += `&parentAccount=${parentAccount}`;
    }

    const result = await InternalApi.get(uri).catch((err: AxiosError) => {
      const error = get(err, "response.data.error") || err.message || "Unknown Error";

      LoggerGroup.emit({ warn: "getCppUri Error" }) // Custom error log
        .emit({ error }) // Log the Axios Error
        .end(); // Send to console immediately

      toastr.error(CPP_ERROR_TITLE, CPP_ERROR_MSG);
    });

    if (!result || (result && result.data && result.data.domain === "")) {
      toastr.error(CPP_ERROR_TITLE, CPP_ERROR_MSG);
    }

    return result;
  };
